// The store report catalog (api-surface Part K) as data, grouped the way
// the API groups it. Every report shares one query grammar and one result
// shape (`Report` in lib/api.ts) — the columns describe themselves — so the
// catalog only names each report, its path under …/reports/, and the
// dimensions it documents (the first is the server's default).
//
// The route segment is the report's file stem — the path with "/" → "-",
// the same stem the server names its CSV/XLSX downloads with
// (sales/summary → sales-summary, settlements/tyro → settlements-tyro).

export type ReportGroup = 'Sales & operations' | 'Money' | 'Staff' | 'Inventory';

export type ReportDef = {
  /** the path under …/reports/ */
  path: string;
  title: string;
  blurb: string;
  group: ReportGroup;
  /** documented groupBy values, the server's default first */
  groupBy?: string[];
};

export const REPORT_GROUPS: ReportGroup[] = ['Sales & operations', 'Money', 'Staff', 'Inventory'];

export const SETTLEMENT_PROVIDERS = ['tyro', 'anz_worldline', 'stripe', 'manual'];

export const REPORTS: ReportDef[] = [
  // ── sales & operations ──
  { path: 'sales/summary', title: 'Sales summary', blurb: 'Orders, revenue, discounts, surcharges, GST — by day, hour, source or collection.', group: 'Sales & operations', groupBy: ['day', 'hour', 'source', 'collection_type'] },
  { path: 'sales/items', title: 'Item sales', blurb: 'Every menu item sold: quantity, gross, discounts, net — or its movement at every depth.', group: 'Sales & operations', groupBy: ['item', 'movement'] },
  { path: 'sales/categories', title: 'Category sales', blurb: 'Quantity and net revenue per category.', group: 'Sales & operations' },
  { path: 'sales/pizzas', title: 'Pizza sales', blurb: 'Pizzas by item or size, whole, as halves and in bundles.', group: 'Sales & operations', groupBy: ['item', 'size'] },
  { path: 'sales/toppings', title: 'Topping sales', blurb: 'Toppings added and removed, with their revenue.', group: 'Sales & operations' },
  { path: 'sales/employees', title: 'Employee sales', blurb: 'Orders, revenue, tendered and tips per employee.', group: 'Sales & operations' },
  { path: 'sales/terminals', title: 'Terminal sales', blurb: 'Orders, revenue, tendered and tips per POS terminal.', group: 'Sales & operations' },
  { path: 'sales/exceptions', title: 'Sales exceptions', blurb: 'Discounts, voids, cancellations and refunds against sales.', group: 'Sales & operations' },
  { path: 'covers', title: 'Covers', blurb: 'Dine-in guests and revenue per cover, by day or hour.', group: 'Sales & operations', groupBy: ['day', 'hour'] },
  { path: 'prep-times', title: 'Prep times', blurb: 'Accept and prep percentiles from the order timeline.', group: 'Sales & operations', groupBy: ['day', 'hour', 'source', 'collection_type'] },
  // ── money ──
  { path: 'tenders', title: 'Tenders', blurb: 'Money moved by transaction, method and provider; net reconciles.', group: 'Money' },
  { path: 'settlements/tyro', title: 'Settlements', blurb: 'Per-payment settlement lines with provider references.', group: 'Money' },
  { path: 'tips', title: 'Tips', blurb: 'Tips by day, provider, employee or method.', group: 'Money', groupBy: ['day', 'provider', 'employee', 'method'] },
  { path: 'exceptions', title: 'Money exceptions', blurb: 'Refunds, discounts and cancellations that moved money.', group: 'Money' },
  { path: 'platform-fees', title: 'Platform fees', blurb: 'Restoplus, processing and Stripe fees by day or order.', group: 'Money', groupBy: ['day', 'order'] },
  { path: 'cash-reconciliation', title: 'Cash reconciliation', blurb: 'Expected against counted cash, day by day.', group: 'Money' },
  // ── staff ──
  { path: 'timesheets', title: 'Timesheets', blurb: 'Shifts with breaks and worked minutes.', group: 'Staff' },
  // ── inventory ──
  { path: 'inventory/consumption', title: 'Consumption', blurb: 'Ingredients used by sales, net of reversals, with cost.', group: 'Inventory' },
  { path: 'inventory/variance', title: 'Stocktake variance', blurb: 'Theoretical against counted stock, with the variance cost.', group: 'Inventory' },
  { path: 'inventory/cogs', title: 'Cost of goods', blurb: 'Revenue, COGS and margin by day or ingredient.', group: 'Inventory', groupBy: ['day', 'ingredient'] },
];

export const slugOf = (path: string): string => path.replace(/\//g, '-');

/** The definition and the API path for a route slug (settlements carry their provider). */
export function reportBySlug(slug: string | undefined): { def: ReportDef; path: string } | null {
  if (!slug) return null;
  const provider = SETTLEMENT_PROVIDERS.find((p) => slug === `settlements-${p}`);
  if (provider) return { def: REPORTS.find((r) => r.path.startsWith('settlements/'))!, path: `settlements/${provider}` };
  const def = REPORTS.find((r) => slugOf(r.path) === slug);
  return def ? { def, path: def.path } : null;
}

export const PRESETS: { key: string; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
];

// ── Legacy: the old-contract report model, kept only because
// restaurant/BrandReports.tsx (restaurant scope, rebuilt by its own
// session) still renders through controls.tsx. Delete both with that
// rebuild — new pages render a `Report` through ./table.tsx.
/** @deprecated old contract */
export type Kind = 'text' | 'money' | 'int' | 'num' | 'qty' | 'secs' | 'time' | 'date';
/** @deprecated old contract */
export type Col = { key: string; label: string; kind?: Kind };
/** @deprecated old contract */
export type Section = { dataKey: string; title?: string; columns: Col[] };
